import { Plus } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@components/page-header';
import {
    CellProps,
    ReactColumn,
    SortingRule,
    TableComponent
} from '@components/table';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { WorkoutTypeRuleDto } from '../../core/api/FitnessActivityClient';
import { useWorkoutValidationRulesStore } from './list-store';
import { links } from '../../core/router';
import { DeleteWorkoutTypeRuleDialog } from './delete-workout-type-rule-dialog';

function formatEnumLabel(value?: string | null): string {
    if (!value) {
        return '-';
    }

    return value
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export const WorkoutValidationRules = observer(() => {
    const navigate = useNavigate();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRule, setSelectedRule] = useState<WorkoutTypeRuleDto | null>(
        null
    );
    const {
        workoutTypeRules,
        sorting,
        setSorting,
        setSearchTerm,
        dispatch,
        searchParams
    } = useWorkoutValidationRulesStore();

    const onSortingChange = (nextSorting?: SortingRule) => {
        if (nextSorting) {
            setSorting(nextSorting);
        }
    };

    const columns: ReactColumn<WorkoutTypeRuleDto>[] = [
        {
            Header: 'Category',
            accessor: (row) => row.workoutCategory,
            id: 'workoutCategory',
            Cell: ({ row: { original } }: CellProps<WorkoutTypeRuleDto>) => (
                <span className="text-sm">
                    {formatEnumLabel(original.workoutCategory)}
                </span>
            )
        },
        {
            Header: 'Type',
            accessor: (row) => row.workoutType,
            id: 'workoutType',
            Cell: ({ row: { original } }: CellProps<WorkoutTypeRuleDto>) => (
                <span className="text-sm">
                    {formatEnumLabel(original.workoutType)}
                </span>
            )
        },
        {
            Header: 'Validation Rules',
            id: 'validationRules',
            disableSortBy: true,
            Cell: ({ row: { original } }: CellProps<WorkoutTypeRuleDto>) => {
                const r = original.validationRule;
                if (!r) {
                    return (
                        <span className="text-sm text-muted-foreground">-</span>
                    );
                }

                const chips: { label: string; value: string }[] = [];
                if (r.minAverageSpeed != null) {
                    chips.push({
                        label: 'Min Avg Spd',
                        value: `${r.minAverageSpeed} km/h`
                    });
                }
                if (r.maxAverageSpeed != null) {
                    chips.push({
                        label: 'Max Avg Spd',
                        value: `${r.maxAverageSpeed} km/h`
                    });
                }
                if (r.maxAllowedSpeed != null) {
                    chips.push({
                        label: 'Max Spd',
                        value: `${r.maxAllowedSpeed} km/h`
                    });
                }
                if (r.minDurationInMinutes != null) {
                    chips.push({
                        label: 'Min Duration',
                        value: `${r.minDurationInMinutes} min`
                    });
                }
                if (r.minDistanceInMeters != null) {
                    chips.push({
                        label: 'Min Dist',
                        value: `${r.minDistanceInMeters} m`
                    });
                }
                if (r.maxDistanceInMeters != null) {
                    chips.push({
                        label: 'Max Dist',
                        value: `${r.maxDistanceInMeters} m`
                    });
                }
                if (r.minCaloriesPerMinute != null) {
                    chips.push({
                        label: 'Min Cal/Min',
                        value: `${r.minCaloriesPerMinute}`
                    });
                }

                if (chips.length === 0) {
                    return (
                        <span className="text-sm text-muted-foreground">-</span>
                    );
                }

                return (
                    <div className="flex flex-wrap gap-1">
                        {chips.map((chip) => (
                            <Badge key={chip.label} variant="outline">
                                {`${chip.label}: ${chip.value}`}
                            </Badge>
                        ))}
                    </div>
                );
            }
        },
        {
            Header: '',
            id: 'actions',
            disableSortBy: true,
            width: 40,
            Cell: () => null
        }
    ];

    return (
        <>
            <PageHeader
                title="Workout Validation Rules"
                actions={
                    <Button
                        onClick={() => navigate(links.createWorkoutTypeRule())}
                    >
                        <Plus />
                        Create
                    </Button>
                }
            />

            <TableComponent
                columns={columns}
                items={workoutTypeRules}
                dense
                tableLayout="auto"
                onRowClick={(rule) =>
                    navigate(links.editWorkoutTypeRule(String(rule.id)))
                }
                onSortingChange={onSortingChange}
                sorting={sorting as SortingRule | undefined}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search by workout type or category..."
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                contextMenu={[
                    {
                        label: 'Edit',
                        action: (rule) => {
                            navigate(
                                links.editWorkoutTypeRule(String(rule.id))
                            );
                        }
                    },
                    {
                        label: 'Delete',
                        action: (rule) => {
                            setSelectedRule(rule);
                            setDeleteDialogOpen(true);
                        }
                    }
                ]}
            />

            <DeleteWorkoutTypeRuleDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                rule={selectedRule}
                onSuccess={() => {
                    workoutTypeRules.request([searchParams]);
                }}
            />
        </>
    );
});
